/**
 * Utility functions for Student Uniform feature
 * Extracted from StudentUniform.jsx for better maintainability and testability
 */

/**
 * Smart filter: Only include modified/new/deleted students
 * Reduces payload size by excluding unchanged students
 * @param {Array} allStudents - All students in the grid
 * @returns {Array} Filtered students (edited, new, or deleted)
 */
export const filterModifiedData = (allStudents) => {
  console.log('uniformHelpers: Smart Filter - Starting filter process...');
  console.log(`uniformHelpers: Total students: ${allStudents.length}`);

  // Filter to include only: edited, new, or deleted students
  const filteredData = allStudents.filter(student => {
    // Include if deleted
    if (student.isDeleted) {
      return true;
    }

    // Include if newly added
    if (student.isNewStudent) {
      return true;
    }

    // Include if edited
    if (student.isEdited) {
      return true;
    }

    return false; // Exclude unchanged students
  });

  // Count by type for logging
  const editedCount = filteredData.filter(s => s.isEdited && !s.isDeleted && !s.isNewStudent).length;
  const newCount = filteredData.filter(s => s.isNewStudent).length;
  const deletedCount = filteredData.filter(s => s.isDeleted).length;

  console.log(`uniformHelpers: Edited students: ${editedCount}`);
  console.log(`uniformHelpers: New students: ${newCount}`);
  console.log(`uniformHelpers: Deleted students: ${deletedCount}`);

  // Show optimization info
  const reductionPercent = allStudents.length > 0
    ? Math.round((1 - filteredData.length / allStudents.length) * 100)
    : 0;
  console.log(`uniformHelpers: Smart Filter - Sending ${filteredData.length} of ${allStudents.length} students (${reductionPercent}% reduction)`);

  return filteredData;
};

/**
 * Comprehensive uniform data validation
 * Checks for duplicate ICs and incomplete uniform selections
 * @param {Array} studentsToValidate - Students to validate
 * @param {Object} sizeConfig - Size configuration from Redux
 * @returns {Object} { duplicateICs, invalidRecords }
 */
export const validateUniformData = (studentsToValidate, sizeConfig) => {
  const validSizes = ['', ...(sizeConfig?.uniformSizes || ['S', 'M', 'L', 'XL', 'XXL', 'XXXL'])];
  const validShoes = ['', ...(sizeConfig?.shoeSizes || ['12', '11', '3', '4', '5', '6', '7', '8', '9', '10'])];

  const duplicateICs = [];
  const invalidRecords = [];
  const icCounts = {};

  // 1. Check for duplicate ICs
  studentsToValidate.forEach((student, index) => {
    if (student.noKP && student.noKP !== 'No IC' && !student.isDeleted) {
      icCounts[student.noKP] = icCounts[student.noKP] || [];
      icCounts[student.noKP].push({ index: index + 1, name: student.name, class: student.class });
    }
  });

  Object.entries(icCounts).forEach(([nokp, students]) => {
    if (students.length > 1) {
      duplicateICs.push({
        nokp,
        students: students.map(s => `${s.name} (Row ${s.index})`).join(', '),
        count: students.length
      });
    }
  });

  // 2. Validate uniform selections (matching old app's comprehensive validation)
  studentsToValidate.forEach((student, index) => {
    if (student.isDeleted) return; // Skip deleted students

    const errors = [];

    if (!student.jantina) {
      invalidRecords.push({
        index: index + 1,
        noKP: student.noKP,
        name: student.name,
        class: student.class,
        error: "Jantina is not set"
      });
      return;
    }

    // MALE VALIDATION
    if (student.jantina.toUpperCase() === 'L') {
      // 1. BajuPutih is MANDATORY
      if (!student.bajuPutih) {
        errors.push("Baju Putih is mandatory");
      } else if (!validSizes.includes(student.bajuPutih)) {
        errors.push(`Baju Putih has invalid size "${student.bajuPutih}"`);
      }

      // 2. EXACTLY ONE of SeluarPanjang OR SeluarPendek
      if (!student.seluarPanjang && !student.seluarPendek) {
        errors.push("Exactly one of Seluar Panjang or Seluar Pendek must be selected");
      } else if (student.seluarPanjang && student.seluarPendek) {
        errors.push("Only one of Seluar Panjang or Seluar Pendek can be selected");
      } else if (student.seluarPanjang && !validSizes.includes(student.seluarPanjang)) {
        errors.push(`Seluar Panjang has invalid size "${student.seluarPanjang}"`);
      } else if (student.seluarPendek && !validSizes.includes(student.seluarPendek)) {
        errors.push(`Seluar Pendek has invalid size "${student.seluarPendek}"`);
      }

      // 3. Kasut is MANDATORY
      if (!student.kasut) {
        errors.push("Kasut is mandatory");
      } else if (!validShoes.includes(student.kasut)) {
        errors.push(`Kasut has invalid size "${student.kasut}"`);
      }

      // 4. Check for gender violations (male cannot have female items)
      if (student.bajuKurung) errors.push("Male cannot have Baju Kurung");
      if (student.kainKurung) errors.push("Male cannot have Kain Kurung");
      if (student.pinafore) errors.push("Male cannot have Pinafore");
    }

    // FEMALE VALIDATION
    else if (student.jantina.toUpperCase() === 'P') {
      // 1. EXACTLY ONE SET: (BajuPutih + Pinafore) OR (BajuKurung + KainKurung)
      const hasBajuPinaforeSet = student.bajuPutih && student.pinafore && !student.bajuKurung && !student.kainKurung;
      const hasKurungSet = student.bajuKurung && student.kainKurung && !student.bajuPutih && !student.pinafore;

      if (!hasBajuPinaforeSet && !hasKurungSet) {
        errors.push("Exactly one of (Baju Putih and Pinafore) or (Baju Kurung and Kain Kurung) must be selected");
      } else if (hasBajuPinaforeSet) {
        // Validate BajuPutih size
        if (!validSizes.includes(student.bajuPutih)) {
          errors.push(`Baju Putih has invalid size "${student.bajuPutih}"`);
        }
        // Validate Pinafore size
        if (!validSizes.includes(student.pinafore)) {
          errors.push(`Pinafore has invalid size "${student.pinafore}"`);
        }
      } else if (hasKurungSet) {
        // Validate BajuKurung size
        if (!validSizes.includes(student.bajuKurung)) {
          errors.push(`Baju Kurung has invalid size "${student.bajuKurung}"`);
        }
        // Validate KainKurung size
        if (!validSizes.includes(student.kainKurung)) {
          errors.push(`Kain Kurung has invalid size "${student.kainKurung}"`);
        }
      }

      // 2. Kasut is MANDATORY
      if (!student.kasut) {
        errors.push("Kasut is mandatory");
      } else if (!validShoes.includes(student.kasut)) {
        errors.push(`Kasut has invalid size "${student.kasut}"`);
      }

      // 3. Check for gender violations (female cannot have male items)
      if (student.seluarPanjang) errors.push("Female cannot have Seluar Panjang");
      if (student.seluarPendek) errors.push("Female cannot have Seluar Pendek");
    }

    if (errors.length > 0) {
      invalidRecords.push({
        index: index + 1,
        noKP: student.noKP,
        name: student.name,
        class: student.class,
        error: errors.join("; ")
      });
    }
  });

  return { duplicateICs, invalidRecords };
};

/**
 * Build HTML message for validation errors
 * Used in SweetAlert2 dialogs for Save/Submit
 * @param {Array} duplicateICs - Array of duplicate IC records
 * @param {Array} deletedStudents - Array of deleted students (null to hide section)
 * @param {Array} invalidRecords - Array of validation error records
 * @returns {String} HTML string for SweetAlert2
 */
export const buildValidationErrorHtml = (duplicateICs, deletedStudents, invalidRecords) => {
  let htmlMessage = '<div style="text-align: left; max-height: 500px; overflow-y: auto;">';

  // 1. Show duplicate IC section (if any)
  if (duplicateICs.length > 0) {
    const dupRows = duplicateICs.map(dup => `
      <tr style="background: #ffebee;">
        <td style="padding: 4px 6px; border-bottom: 1px solid #ddd; font-weight: bold; font-size: 12px;">${dup.nokp}</td>
        <td style="padding: 4px 6px; border-bottom: 1px solid #ddd; font-size: 12px;">${dup.students}</td>
        <td style="padding: 4px 6px; border-bottom: 1px solid #ddd; text-align: center; font-size: 12px;">${dup.count}</td>
      </tr>
    `).join('');

    htmlMessage += `
      <div style="background: #ffebee; border-left: 5px solid #f44336; border-radius: 8px; margin-bottom: 15px; overflow: hidden;">
        <div style="background: #f44336; padding: 10px 15px; display: flex; align-items: center; justify-content: space-between;">
          <span style="color: white; font-weight: 600; font-size: 14px;">⚠ Duplicate IC Numbers</span>
          <span style="background: white; color: #f44336; padding: 4px 12px; border-radius: 12px; font-weight: bold; font-size: 13px;">${duplicateICs.length}</span>
        </div>
        <div style="padding: 10px 15px; background: white;">
          <table style="width: 100%; border-collapse: collapse; font-size: 12px;">
            <thead>
              <tr style="background: #f5f5f5;">
                <th style="padding: 6px 8px; border-bottom: 2px solid #ddd; text-align: left; font-size: 12px;">IC Number</th>
                <th style="padding: 6px 8px; border-bottom: 2px solid #ddd; text-align: left; font-size: 12px;">Students</th>
                <th style="padding: 6px 8px; border-bottom: 2px solid #ddd; text-align: center; font-size: 12px;">Count</th>
              </tr>
            </thead>
            <tbody>${dupRows}</tbody>
          </table>
        </div>
      </div>
    `;
  }

  // 2. Show deleted students section (if any and not hidden)
  if (deletedStudents && deletedStudents.length > 0) {
    const delRows = deletedStudents.map(del => `
      <tr style="background: #fff8e1;">
        <td style="padding: 4px 6px; border-bottom: 1px solid #ddd; font-size: 12px;">${del.name}</td>
        <td style="padding: 4px 6px; border-bottom: 1px solid #ddd; font-size: 12px;">${del.noKP || 'No IC'}</td>
        <td style="padding: 4px 6px; border-bottom: 1px solid #ddd; font-size: 12px;">${del.class || '-'}</td>
      </tr>
    `).join('');

    htmlMessage += `
      <div style="background: #fff8e1; border-left: 5px solid #fbc02d; border-radius: 8px; margin-bottom: 15px; overflow: hidden;">
        <div style="background: #fbc02d; padding: 10px 15px; display: flex; align-items: center; justify-content: space-between;">
          <span style="color: #333; font-weight: 600; font-size: 14px;">🗑️ Deleted Students</span>
          <span style="background: white; color: #f57f17; padding: 4px 12px; border-radius: 12px; font-weight: bold; font-size: 13px;">${deletedStudents.length}</span>
        </div>
        <div style="padding: 10px 15px; background: white;">
          <table style="width: 100%; border-collapse: collapse; font-size: 12px;">
            <thead>
              <tr style="background: #f5f5f5;">
                <th style="padding: 6px 8px; border-bottom: 2px solid #ddd; text-align: left; font-size: 12px;">Name</th>
                <th style="padding: 6px 8px; border-bottom: 2px solid #ddd; text-align: left; font-size: 12px;">IC</th>
                <th style="padding: 6px 8px; border-bottom: 2px solid #ddd; text-align: left; font-size: 12px;">Class</th>
              </tr>
            </thead>
            <tbody>${delRows}</tbody>
          </table>
        </div>
      </div>
    `;
  }

  // 3. Show incomplete uniforms section (if any)
  if (invalidRecords.length > 0) {
    const invRows = invalidRecords.map(record => `
      <tr style="background: #ffebee;">
        <td style="padding: 4px 6px; border-bottom: 1px solid #ddd; text-align: center; font-size: 12px;">${record.index}</td>
        <td style="padding: 4px 6px; border-bottom: 1px solid #ddd; font-size: 12px;">${record.name}</td>
        <td style="padding: 4px 6px; border-bottom: 1px solid #ddd; font-size: 12px;">${record.noKP}</td>
        <td style="padding: 4px 6px; border-bottom: 1px solid #ddd; font-size: 12px;">${record.class}</td>
        <td style="padding: 4px 6px; border-bottom: 1px solid #ddd; color: #dc3545; font-size: 12px;">${record.error}</td>
      </tr>
    `).join('');

    htmlMessage += `
      <div style="background: #ffebee; border-left: 5px solid #f44336; border-radius: 8px; margin-bottom: 15px; overflow: hidden;">
        <div style="background: #f44336; padding: 10px 15px; display: flex; align-items: center; justify-content: space-between;">
          <span style="color: white; font-weight: 600; font-size: 14px;">👕 Incomplete Uniform Selections</span>
          <span style="background: white; color: #f44336; padding: 4px 12px; border-radius: 12px; font-weight: bold; font-size: 13px;">${invalidRecords.length}</span>
        </div>
        <div style="padding: 10px 15px; background: white;">
          <table style="width: 100%; border-collapse: collapse; font-size: 12px;">
            <thead>
              <tr style="background: #f5f5f5;">
                <th style="padding: 6px 8px; border-bottom: 2px solid #ddd; text-align: center; font-size: 12px;">Row</th>
                <th style="padding: 6px 8px; border-bottom: 2px solid #ddd; text-align: left; font-size: 12px;">Name</th>
                <th style="padding: 6px 8px; border-bottom: 2px solid #ddd; text-align: left; font-size: 12px;">IC</th>
                <th style="padding: 6px 8px; border-bottom: 2px solid #ddd; text-align: left; font-size: 12px;">Class</th>
                <th style="padding: 6px 8px; border-bottom: 2px solid #ddd; text-align: left; font-size: 12px;">Error</th>
              </tr>
            </thead>
            <tbody>${invRows}</tbody>
          </table>
        </div>
      </div>
    `;
  }

  htmlMessage += '</div>';
  return htmlMessage;
};

/**
 * Get CSS class for cell based on gender and mutual exclusivity rules
 * @param {Object} params - AG-Grid cell params
 * @returns {String} CSS class name
 */
export const getCellClass = (params) => {
  const field = params.colDef.field;
  const rowData = params.data;
  const gender = rowData?.jantina;

  if (!rowData || !field) return '';

  // Check if field should be disabled
  if (gender === 'L') {
    // Male students - disable female uniform fields
    if (['bajuKurung', 'kainKurung', 'pinafore'].includes(field)) {
      return 'cell-disabled';
    }
    // Male Mutual Exclusivity: SeluarPanjang XOR SeluarPendek
    if (field === 'seluarPanjang' && rowData.seluarPendek) {
      return 'cell-disabled';
    }
    if (field === 'seluarPendek' && rowData.seluarPanjang) {
      return 'cell-disabled';
    }
  } else if (gender === 'P') {
    // Female students - disable male uniform fields
    if (['seluarPanjang', 'seluarPendek'].includes(field)) {
      return 'cell-disabled';
    }
    // Female Mutual Exclusivity
    const hasSet1 = rowData.bajuPutih || rowData.pinafore;
    const hasSet2 = rowData.bajuKurung || rowData.kainKurung;

    if (hasSet1 && ['bajuKurung', 'kainKurung'].includes(field)) {
      return 'cell-disabled';
    }
    if (hasSet2 && ['bajuPutih', 'pinafore'].includes(field)) {
      return 'cell-disabled';
    }
  }

  return '';
};

/**
 * Get cell style (highlight edited/deleted cells)
 * @param {Object} params - AG-Grid cell params
 * @returns {Object} Style object
 */
export const getCellStyle = (params) => {
  const style = { textAlign: 'center' };
  const rowData = params.data;

  if (!rowData) return style;

  // Mark deleted rows (highest priority)
  if (rowData.isDeleted) {
    style.backgroundColor = '#ffebee'; // Light red
    style.textDecoration = 'line-through';
    return style;
  }

  // Highlight edited cells
  if (rowData.isEdited) {
    style.backgroundColor = '#fffacd'; // Light yellow
  }

  return style;
};
